import React, { useEffect, useMemo } from "react";
import { Icon } from "antd";
import { connect, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import * as R from "ramda";

import Header from "../Header";
import Filters from "../transforms/Filters";
import Pagination from "../transforms/Pagination";
import { hydratedTeams } from "../../store/selectors/team";
import { canCreateOpportunity } from "../../store/selectors/user";
import { FETCH_TEAMS } from "../../store/actions/teams";
import { PAGE_SIZE } from "../../constants/opportunity";
import { isLoading } from "../../utils/actions";

import TeamCard from "./Card";
import SkeletonTeamList from "./SkeletonTeamList";

import styled from "styled-components/macro";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  grid-gap: 24px;
  @media screen and (-ms-high-contrast: active),
    screen and (-ms-high-contrast: none) {
    /* IE10+ specific styles go here */
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex-wrap: wrap;
    > a {
      width: 32%;
      margin-bottom: 18px;
    }
  }
`;

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  font-size: 1.5em;
  i {
    font-size: 60px;
    margin-bottom: 20px;
    opacity: 0.3;
  }
`;

const TeamList = ({
  company,
  teams,
  loading,
  error,
  loadingOpportunities,
  canCreate
}) => {
  const { t } = useTranslation(["teams", "global"]);
  const dispatch = useDispatch();

  const teamsLoading = useMemo(() => loading || teams === null, [
    loading,
    teams
  ]);

  useEffect(() => {
    return () => {
      dispatch(FETCH_TEAMS.clearStatus());
    };
  }, [dispatch]);

  return (
    <div data-test="teams-list">
      <Header
        title={t("global:teams")}
        link={canCreate ? "/equipos/nuevo" : undefined}
        label={t("teams:newTeam")}
      />
      <Filters data={teams || []} searchProp="name">
        {({ data: teams }) => {
          if (error) {
            return (
              <Placeholder data-test="teams-error">
                <Icon type="warning" />
                {t("teams:fetchError")}
              </Placeholder>
            );
          }

          if (teamsLoading) return <SkeletonTeamList />;

          if (!teams.length)
            return (
              <Placeholder data-test="teams-empty">
                <Icon type="file-search" />
                {t("teams:noTeams")}
              </Placeholder>
            );

          return (
            <Pagination
              data={R.sort(R.descend(R.propOr(0, "createdAt")), teams)}
              pageSize={PAGE_SIZE}
            >
              {({ data: teams }) => (
                <Grid>
                  {teams.map(team => (
                    <TeamCard
                      key={team.id}
                      isLoadingOpportunities={loadingOpportunities}
                      companyName={company.name}
                      {...team}
                    />
                  ))}
                </Grid>
              )}
            </Pagination>
          );
        }}
      </Filters>
    </div>
  );
};

export default connect(state => ({
  loadingOpportunities: !R.path(["opportunities", "list"], state),
  company: state.company,
  canCreate: canCreateOpportunity(state.user),
  teams: state.teams && hydratedTeams(state),
  loading: isLoading(FETCH_TEAMS, state),
  error: FETCH_TEAMS.getError(state)
}))(TeamList);
